import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import dayjs from '../../shared/others/dayjs';
import { PalantirIconComponent } from '../icon/icon.component';
import type { WeekDays } from './calendar.type';

@Component({
  selector: 'palantir-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, PalantirIconComponent],
})
export class PalantirCalendarComponent implements OnInit {
  @Input() public set selectedDays(value: string[]) {
    this._selectedDays = value.map((dateStr) => dayjs(dateStr));
  }

  @Input() public language: string = 'pt-br';

  @Input() public selectable: boolean = false;

  @Output() public onClick = new EventEmitter<string>();

  public weekDays: WeekDays = {};

  public weekDaysKeys: string[] = [];

  public currentDateShowing = dayjs();

  private _selectedDays: dayjs.Dayjs[] = [];

  public ngOnInit() {
    dayjs.locale(this.language);
    this.weekDaysKeys = this.getWeekDaysKeys();
    this.weekDays = this.getDaysByWeekday();
  }

  public getDaysByWeekday(): WeekDays {
    const month = this.currentDateShowing.month() + 1;
    const year = this.currentDateShowing.format('YYYY');

    const firstDayOfMonth = dayjs(`${year}-${month}-01`);
    const daysInMonth = firstDayOfMonth.daysInMonth();
    const startDay = firstDayOfMonth.day();

    const weekdays: WeekDays = this.weekDaysKeys.reduce<WeekDays>((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});

    const today = dayjs();

    const prevMonth = firstDayOfMonth.subtract(1, 'month');
    const daysInPrevMonth = prevMonth.daysInMonth();

    for (let startDayOffset = startDay - 1; startDayOffset >= 0; startDayOffset--) {
      const day = daysInPrevMonth - startDayOffset;
      const date = prevMonth.date(day);
      weekdays[date.format('dddd').toLowerCase()].push({
        value: day,
        date,
        isToday: date.isSame(today, 'day'),
        isSelected: this.isSelected(date),
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = firstDayOfMonth.date(day);
      weekdays[date.format('dddd').toLowerCase()].push({
        value: day,
        date,
        isCurrentMonth: true,
        isToday: date.isSame(today, 'day'),
        isSelected: this.isSelected(date),
      });
    }

    let nextMonthDay = 1;
    while (Object.values(weekdays).some((days: any[]) => days.length < 6)) {
      const date = firstDayOfMonth.add(daysInMonth + nextMonthDay - 1, 'day');
      weekdays[date.format('dddd').toLowerCase()].push({
        value: nextMonthDay,
        date,
        isToday: date.isSame(today, 'day'),
        isSelected: this.isSelected(date),
      });
      nextMonthDay++;
    }

    return weekdays;
  }

  public getWeekDaysKeys(): string[] {
    const weekDays = [];
    for (let currentIndex = 0; currentIndex < 7; currentIndex++) {
      weekDays.push(dayjs().day(currentIndex).format('dddd').toLowerCase());
    }

    return weekDays;
  }

  public renderNextMonth() {
    this.currentDateShowing = this.currentDateShowing.add(1, 'month');
    this.weekDays = this.getDaysByWeekday();
  }

  public renderPreviousMonth() {
    this.currentDateShowing = this.currentDateShowing.subtract(1, 'month');
    this.weekDays = this.getDaysByWeekday();
  }

  public getCurrentMonthName(): string {
    const monthName = this.currentDateShowing.format('MMMM');
    return (monthName.charAt(0).toUpperCase() + monthName.slice(1)).slice(0, 3);
  }

  public getCurrentYear(): string {
    return this.currentDateShowing.format('YYYY');
  }

  public getCurrentFormattedDate(): string {
    return `${this.getCurrentMonthName()}. de ${this.getCurrentYear()}`;
  }

  public isMostOldSelectedDay(date: dayjs.Dayjs): boolean {
    const mostOldDates = this._selectedDays.filter((selectedDate) =>
      selectedDate.isBefore(date, 'day'),
    );

    return !mostOldDates.length;
  }

  public isMostRecentSelectedDay(date: dayjs.Dayjs): boolean {
    const mostRecentDates = this._selectedDays.filter((selectedDate) =>
      selectedDate.isAfter(date, 'day'),
    );

    return !mostRecentDates.length;
  }

  public isSelected(date: dayjs.Dayjs): boolean {
    return this._selectedDays.some((selectedDate) => selectedDate.isSame(date, 'day'));
  }

  public isToday(date: dayjs.Dayjs): boolean {
    return date.isSame(dayjs(), 'day');
  }

  public onDayClick(date: dayjs.Dayjs): void {
    this._selectedDays = [date];
    this.onClick.emit(date.format('YYYY-MM-DD'));
  }

  public trackByFn(index: number): number {
    return index;
  }
}
